/**
 * My Membership — what a member has paid, what's coming, what it's worth toward an
 * upgrade, and how to stop. Reads /api/membership/my-plan; every figure comes from the
 * backend engine so this page never does money maths of its own.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, Clock, AlertTriangle, CreditCard, Loader2, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { API } from '@/App';
import TierPaymentModal from '@/components/TierPaymentModal';
import MembershipCard from '@/components/MembershipCard';

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const day = (value) => (value
  ? new Date(value).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
  : '—');

const statusIcon = {
  paid: <CheckCircle2 className="w-4 h-4" style={{ color: '#2a6a3a' }} />,
  scheduled: <Clock className="w-4 h-4" style={{ color: '#8a8a86' }} />,
  failed: <AlertTriangle className="w-4 h-4" style={{ color: '#a02828' }} />,
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:wght@400;600&family=Outfit:wght@300;400;500;600&display=swap');
  .mm-root {
    --forest:#1b3a2d; --gold:#c8a84a; --cream:#f8f5ee; --surface:#ffffff;
    --border:#ddd8cc; --ink:#1a1a18; --ink-mid:#4a4a46; --ink-light:#8a8a86;
    --green:#2a6a3a; --green-bg:#eaf5ea; --red:#a02828; --red-bg:#fdf0f0;
    --amber:#9a6010; --amber-bg:#fef9ea;
    font-family:'Outfit',sans-serif; background:var(--cream); color:var(--ink); min-height:100vh;
  }
  .mm-wrap { max-width: 860px; margin: 0 auto; padding: 28px 20px 64px; }
  .mm-title { font-family:'Crimson Pro',serif; font-size:2rem; margin:0; }
  .mm-card { background:var(--surface); border:1px solid var(--border); border-radius:14px;
             padding:22px; margin-bottom:20px; }
  .mm-card h2 { font-family:'Crimson Pro',serif; font-size:1.35rem; margin:0 0 4px; }
  .mm-sub { color:var(--ink-light); font-size:.875rem; margin:0 0 16px; }
  .mm-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:14px; }
  .mm-stat { border:1px solid var(--border); border-radius:10px; padding:12px 14px; }
  .mm-stat span { display:block; font-size:.75rem; color:var(--ink-light); text-transform:uppercase;
                  letter-spacing:.06em; margin-bottom:4px; }
  .mm-stat strong { font-size:1.15rem; font-weight:600; }
  .mm-row { display:flex; align-items:center; justify-content:space-between; gap:12px;
            padding:11px 0; border-bottom:1px solid var(--border); font-size:.9rem; }
  .mm-row:last-child { border-bottom:0; }
  .mm-flag { border-radius:10px; padding:14px 16px; margin-bottom:18px; font-size:.9rem; }
  .mm-note { font-size:.82rem; color:var(--ink-light); line-height:1.55; }
  .mm-actions { display:flex; flex-wrap:wrap; gap:10px; margin-top:18px; }
  .mm-back { display:inline-flex; align-items:center; gap:6px; color:var(--forest);
             text-decoration:none; font-size:.875rem; margin-bottom:18px; }
`;

const MyMembership = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Someone sent here from a locked feature arrives with where they were and the tier that
  // unlocks it, so they get the prices straight away and a way back to what they were doing.
  const cameFrom = params.get('from') || '';
  const cameFromLabel = params.get('from_label') || 'where you left off';
  const suggestedTier = params.get('upgrade') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(Boolean(suggestedTier));

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/membership/my-plan`, auth());
      setData(res.data);
    } catch (error) {
      toast.error('Could not load your membership');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const post = async (path, label, body = {}) => {
    setBusy(path);
    try {
      await axios.post(`${API}/membership/${path}`, body, auth());
      toast.success(label);
      await load();
      if (path === 'cancel') setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'That did not go through');
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return (
      <div className="mm-root">
        <style>{styles}</style>
        <div className="mm-wrap"><Loader2 className="w-6 h-6 animate-spin" /></div>
      </div>
    );
  }

  const plan = data?.plan;
  const paidTier = !['bronze', 'free_comp'].includes(data?.tier);
  // Bronze needs the tier chooser most of all — it is the only way they learn what an upgrade
  // costs and what it opens.
  const canUpgrade = data?.tier !== 'platinum' && data?.tier !== 'free_comp';

  return (
    <div className="mm-root" data-testid="my-membership-page">
      <style>{styles}</style>
      <div className="mm-wrap">
        <Link to="/dashboard/settings" className="mm-back"><ArrowLeft className="w-4 h-4" /> Settings</Link>
        {cameFrom && (
          <div>
            <button type="button" className="mm-back" onClick={() => navigate(cameFrom)}
                    style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                    data-testid="membership-return-link">
              <ArrowLeft className="w-4 h-4" /> Back to {cameFromLabel}
            </button>
          </div>
        )}
        <h1 className="mm-title">My Membership</h1>
        <p className="mm-sub">Everything you've paid, everything that's coming, and what it's worth.</p>

        {data?.is_frozen && (
          <div className="mm-flag" style={{ background: 'var(--red-bg)', border: '1px solid #f0c8c8' }}
               data-testid="membership-frozen-banner">
            <strong style={{ color: 'var(--red)' }}>Your access is on Bronze for now</strong>
            <p className="mm-note" style={{ marginTop: 4 }}>
              {data.frozen_reason
                ? `We couldn't collect your payment — ${data.frozen_reason}.`
                : 'A payment could not be collected.'}{' '}
              Nothing is lost — pay what's
              outstanding and your access comes straight back, exactly where you left off. There's no
              deadline and no penalty.
            </p>
          </div>
        )}

        <div className="mm-card">
          <h2>Where you stand</h2>
          <p className="mm-sub">Your tier, your plan, and the credit you hold.</p>
          <div className="mm-grid">
            <div className="mm-stat"><span>Tier</span><strong style={{ textTransform: 'capitalize' }}>{data?.tier}</strong></div>
            <div className="mm-stat">
              <span>Plan</span>
              <strong>{plan ? `${plan.payments_made} of ${plan.plan_count} paid` : 'No plan'}</strong>
            </div>
            <div className="mm-stat"><span>Paid so far (base)</span><strong>{money(data?.base_paid_on_current_tier)}</strong></div>
            <div className="mm-stat" data-testid="membership-credit">
              <span>Upgrade credit</span><strong>{money(data?.credit_toward_upgrade)}</strong>
            </div>
          </div>
          <p className="mm-note" style={{ marginTop: 14 }}>
            Your credit is the base you've paid on your current tier — it comes straight off the price
            of a higher tier. Fees and tax are never part of it.
          </p>
          {canUpgrade && (
            <div className="mm-actions">
              <Button onClick={() => setShowUpgrade(true)} data-testid="membership-upgrade-btn">
                {paidTier ? 'See what an upgrade costs' : 'See membership tiers and pricing'}
              </Button>
            </div>
          )}
        </div>

        {plan && (
          <div className="mm-card">
            <h2>Your payments</h2>
            <p className="mm-sub">
              {plan.kind === 'lump'
                ? 'You paid in full — nothing further is owed on your membership.'
                : `Payment one was taken at checkout; the rest fall on the same day each month.`}
            </p>
            {plan.schedule.map((entry) => (
              <div className="mm-row" key={entry.number} data-testid={`membership-schedule-row-${entry.number}`}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {statusIcon[entry.status] || statusIcon.scheduled}
                  Payment {entry.number}
                  <span style={{ color: 'var(--ink-light)' }}>· {day(entry.due_date)}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong>{money(entry.billed)}</strong>
                  <span style={{ color: entry.status === 'failed' ? 'var(--red)' : 'var(--ink-light)' }}>
                    {entry.status === 'paid' ? 'paid' : entry.status === 'failed' ? 'declined' : 'scheduled'}
                  </span>
                </span>
              </div>
            ))}

            {data?.upcoming && !data?.outstanding && (
              <>
                <div className="mm-flag" style={{ background: '#eef4fb', border: '1px solid #cfe0f2', marginTop: 18 }}
                     data-testid="membership-upcoming">
                  <strong>Next payment {money(data.upcoming.billed)} on {day(data.upcoming.due_date)}</strong>
                  <p className="mm-note" style={{ marginTop: 4 }}>
                    We'll take it from your saved card automatically, and email you 3 days beforehand.
                    Nothing to do.
                  </p>
                </div>
                {data.pay_remainder && (
                  <div className="mm-actions">
                    <Button variant="outline"
                            onClick={() => post('pay-remainder', 'Your balance is cleared — thank you')}
                            disabled={busy === 'pay-remainder'} data-testid="membership-pay-remainder-btn">
                      Clear the balance early — {money(data.pay_remainder.total)}
                    </Button>
                  </div>
                )}
                {data.pay_remainder && (
                  <p className="mm-note" style={{ marginTop: 10 }}>{data.pay_remainder.note}</p>
                )}
              </>
            )}

            {data?.outstanding && (
              <>
                <div className="mm-flag" style={{ background: 'var(--amber-bg)', border: '1px solid #f0e0b8', marginTop: 18 }}>
                  <strong style={{ color: 'var(--amber)' }}>
                    {money(data.outstanding.billed)} outstanding
                  </strong>
                  <p className="mm-note" style={{ marginTop: 4 }}>
                    Payment {data.outstanding.number}, due {day(data.outstanding.due_date)}.
                    {data.outstanding.attempts > 0 && ` We've tried ${data.outstanding.attempts} time(s).`}
                  </p>
                </div>
                <div className="mm-actions">
                  <Button onClick={() => post('pay-now', 'Payment received — thank you')}
                          disabled={busy === 'pay-now'} data-testid="membership-pay-now-btn">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay {money(data.outstanding.billed)} now
                  </Button>
                  {data.pay_remainder && (
                    <Button variant="outline"
                            onClick={() => post('pay-remainder', 'Your balance is cleared — thank you')}
                            disabled={busy === 'pay-remainder'} data-testid="membership-pay-remainder-btn">
                      Clear the balance — {money(data.pay_remainder.total)}
                    </Button>
                  )}
                </div>
                {data.pay_remainder && (
                  <p className="mm-note" style={{ marginTop: 10 }}>{data.pay_remainder.note}</p>
                )}
                {!data.saved_card_on_file && (
                  <p className="mm-note" style={{ marginTop: 10, color: 'var(--red)' }}>
                    We don't have a card on file for you — contact us and we'll help you settle this.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {(paidTier || data?.saved_card_on_file) && (
          <div className="mm-card" data-testid="membership-card-card">
            <h2>Your card on file</h2>
            <p className="mm-sub">Used for your scheduled payments and the annual maintenance fee.</p>
            <MembershipCard onChanged={load} />
          </div>
        )}

        {paidTier && (
          <div className="mm-card">
            <h2>Annual maintenance</h2>
            <p className="mm-sub">Keeps your access, your materials and your community alive year to year.</p>
            <div className="mm-grid">
              <div className="mm-stat"><span>Amount</span><strong>{money(data?.maintenance?.amount)}</strong></div>
              <div className="mm-stat"><span>Next due</span><strong>{day(data?.maintenance?.next_due)}</strong></div>
              <div className="mm-stat"><span>Member since</span><strong>{day(data?.maintenance?.anniversary_set_on)}</strong></div>
            </div>
            <p className="mm-note" style={{ marginTop: 14 }}>
              Your anniversary never moves — upgrading doesn't reset it, and neither does a missed payment.
            </p>
          </div>
        )}

        {paidTier && (
          <div className="mm-card">
            <h2>Cancelling</h2>
            <p className="mm-sub">Your choice, no waiting period, no hard feelings.</p>
            <p className="mm-note">
              Access ends immediately and any remaining payments are never owed.
              {data?.refund?.eligible
                ? ` You're still inside the 7-day window, so we'll refund ${money((data.refund.refund_base_cents || 0) / 100)} — the base you paid, not the fees or tax.`
                : ' The 7-day refund window has passed, so no refund applies.'}
              {' '}Your {money(data?.credit_toward_upgrade)} credit is forfeited, and coming back later
              starts fresh at full price.
            </p>
            <div className="mm-actions">
              <Button variant="outline" onClick={() => setShowCancel(true)} data-testid="membership-cancel-btn">
                <XCircle className="w-4 h-4 mr-2" /> Cancel my membership
              </Button>
            </div>
          </div>
        )}

        <Dialog open={showCancel} onOpenChange={setShowCancel}>
          <DialogContent data-testid="membership-cancel-dialog">
            <DialogHeader>
              <DialogTitle>Cancel your membership?</DialogTitle>
              <DialogDescription>
                Your access ends right away and your {money(data?.credit_toward_upgrade)} credit is
                forfeited. {data?.refund?.eligible
                  ? `We'll refund ${money((data.refund.refund_base_cents || 0) / 100)}.`
                  : 'No refund applies at this point.'} This can't be undone from here.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCancel(false)}>Keep my membership</Button>
              <Button
                onClick={() => { setShowCancel(false); post('cancel', 'Your membership has been cancelled', { confirm: true }); }}
                disabled={busy === 'cancel'}
                data-testid="membership-confirm-cancel-btn"
              >
                Yes, cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <TierPaymentModal
          isOpen={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          onSuccess={() => { if (cameFrom) navigate(cameFrom); else load(); }}
          currentTier={data?.tier}
          suggestedTier={suggestedTier || null}
          returnTo={cameFrom || null}
          returnLabel={cameFromLabel}
        />
      </div>
    </div>
  );
};

export default MyMembership;
